import { useMemo } from 'react';
import _ from 'lodash';
import styled from 'styled-components';

import Content from '../layout/Content';
import DetailsSection from '../layout/DetailsSection';
import ItemsTable from '../tables/ItemsTable';
import {
  asCurrency,
  asCurrencyWithCents,
  asDynamicDecimal,
  asQuantity,
} from '../../utils/formatters';
import {
  useDataTransforms,
  useFarmName,
  useQueryWithFarmId,
} from '../../utils/hooks';
import { useSubsidySwitchContext } from '../../contexts/SubsidySwitchContext';
import MetricsSummary from './MetricsSummary';
import { SectionGroupsQuery } from '../../queries';
import { useFilterContext } from '../../contexts/FilterContext';
import chartTypes from '../../constants/chartTypes';
import { safeDivide } from '../../utils/utils';
import { useUnits } from '../../contexts/UnitContext';
import { LONG_AREA_UNITS } from '../../constants/units';
import SummarySection from '../layout/SummarySection';

const ItemsTableLastRowBold = styled(ItemsTable)`
  tr:last-child * {
    font-weight: bold;
  }
`;

const SubtitleNote = styled.div`
  margin: var(--cell-padding);
  font-style: italic;
`;

const useColumns = () => {
  const { displayUnit: areaUnitName } = useUnits(LONG_AREA_UNITS);
  const singularAreaUnitName = areaUnitName.replace(/s$/, '');

  return [
    { displayString: 'Crop', accessor: 'cropName' },
    { displayString: 'Stems', accessor: 'stems', format: asQuantity },
    {
      displayString: _.capitalize(areaUnitName),
      accessor: 'area',
      format: asDynamicDecimal,
    },
    {
      displayString: 'Developer Installation',
      accessor: 'installationCost',
      format: asCurrency,
    },
    {
      displayString: 'Genetic Stock',
      accessor: 'geneticStockCost',
      format: asCurrency,
    },
    {
      displayString: 'Total Quoted Cost',
      accessor: 'totalCost',
      format: asCurrency,
    },
    {
      displayString: 'Cost/Stem',
      accessor: 'costPerStem',
      format: asCurrencyWithCents,
    },
    {
      displayString: `Cost/${_.capitalize(singularAreaUnitName)}`,
      accessor: 'costPerArea',
      format: asCurrencyWithCents,
    },
  ];
};

const ALL_CROPS_TEXT = 'Total';
const ALL_CROPS_ACCESSOR = 'ALL_CROPS_ACCESSOR';

const getSummaryData = (convertedData) =>
  convertedData.reduce(
    (acc, { id, totalCost }) => ({
      ...acc,
      [id]: totalCost,
    }),
    {
      [ALL_CROPS_ACCESSOR]: _.sum(
        convertedData.map(({ totalCost }) => totalCost),
      ),
    },
  );

const getSummaryMetrics = (convertedData) =>
  convertedData.map(({ color, id, cropName }) => ({
    displayName: cropName,
    accessor: id,
    color,
    defaultShown: true,
    shown: true,
    format: asCurrency,
  }));

const ALL_CROPS_METRIC = {
  isAggregate: true, // hide this from pie chart
  accessor: ALL_CROPS_ACCESSOR,
  displayName: ALL_CROPS_TEXT,
  format: asCurrency,
};

const header = 'Quote Summary';

const QuoteSummary = ({ dataByCrop, msg }) => {
  const farmName = useFarmName();

  const columns = useColumns();

  const { shouldShowSubsidies } = useSubsidySwitchContext();

  const {
    state: { sectionGroupId },
  } = useFilterContext();
  const sectionGroups = useQueryWithFarmId(SectionGroupsQuery, {
    fetchPolicy: 'cache-and-network',
  });
  const sectionGroupsById = useMemo(
    () => _.keyBy(sectionGroups.data?.farm?.sectionGroups ?? [], 'id'),
    [sectionGroups],
  );

  const sectionGroupName = sectionGroupsById[sectionGroupId]?.name;

  const [summaryData, summaryMetrics] = useDataTransforms(dataByCrop, [
    getSummaryData,
    getSummaryMetrics,
  ]);

  const tableData = useMemo(() => {
    // Add totals:
    const installationCost = _.sumBy(dataByCrop, 'installationCost');
    const geneticStockCost = _.sumBy(dataByCrop, 'geneticStockCost');
    const totalCost = _.sumBy(dataByCrop, 'totalCost');
    const stems = _.sumBy(dataByCrop, 'stems');
    const area = _.sumBy(dataByCrop, 'area');

    return [
      ...dataByCrop,
      // Add totals (we can't use showGroupTotal for this since we're computing averages in this column)
      {
        id: ALL_CROPS_ACCESSOR,
        cropName: ALL_CROPS_TEXT,
        installationCost,
        geneticStockCost,
        totalCost,
        stems,
        area,
        costPerStem: safeDivide(totalCost, stems),
        costPerArea: safeDivide(totalCost, area),
      },
    ];
  }, [dataByCrop]);

  return (
    <Content>
      <SummarySection>
        <MetricsSummary
          metrics={[ALL_CROPS_METRIC, ...summaryMetrics]}
          data={summaryData}
          msg={msg}
          chartType={chartTypes.SIMPLE_PIE}
        />
      </SummarySection>
      <DetailsSection>
        <ItemsTableLastRowBold
          tableHeader={
            <>
              <h3>{header}</h3>
              <SubtitleNote>
                Note: stock and installation costs do not include replants.
              </SubtitleNote>
            </>
          }
          data={tableData}
          cols={columns}
          msg={msg}
          csvFileName={[
            header,
            ...(sectionGroupName ? [sectionGroupName] : []),
            ...(shouldShowSubsidies ? ['subsidies applied'] : []),
            farmName,
          ].join(' - ')}
        />
      </DetailsSection>
    </Content>
  );
};

export default QuoteSummary;
